import React, { useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

// import TextField from '@mui/material/TextField';
import { MultilineFormTextField } from './FormTextField';
import { sharedClasses } from './sharedClasses';
import { BODY_TEXT, DIVIDER } from '../../../ThemeContext/constants';
import { theme } from '../../../ThemeContext/ThemeObject';

import type { IApplicantQuestion } from '../Utilities/QuestionFieldGenerator';

const FormRadioButtonWithTextInputGroup = ({
  options,
  onChange,
  defaultValue,
  groupLabel,
  directionRow,
  innerRef,
  error,
  color,
  draggable,
  question,
  inputText,
  setApprovalFormQuestions
}: {
  options: { label: string; value: string | number }[];
  onChange: (value: string | number) => void;
  directionRow?: boolean;
  defaultValue?: string | number;
  groupLabel?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  error?: string;
  color?: string;
  draggable?: boolean;
  question: IApplicantQuestion;
  inputText: string;
  setApprovalFormQuestions?: (
    value:
      | Record<string, { data: string; inputText: string }>
      | ((
          prev: Record<string, { data: string; inputText: string }>
        ) => Record<string, { data: string; inputText: string }>)
  ) => void;
}) => {
  let initialValue: string | number = '';
  if (defaultValue) initialValue = defaultValue;
  const [value, setValue] = useState(initialValue);
  const [trackedDefault, setTrackedDefault] = useState(defaultValue);
  if (defaultValue !== trackedDefault) {
    setTrackedDefault(defaultValue);
    setValue(defaultValue ?? '');
  }
  // const [inputValue, setInputValue] = useState('');

  const onInternalChange = (nextValue: string) => {
    setValue(nextValue);
    onChange(nextValue);
  };

  const radioStyle = {
    color: DIVIDER,
    '&.Mui-checked': {
      color: theme.palette.action.main
    },
    '& .MuiSvgIcon-root': {
      fontSize: '22px'
    }
  };

  let labelColor = `${BODY_TEXT} !important`;
  if (color) labelColor = color;

  const labelStyle = {
    '& .MuiTypography-root': {
      color: labelColor,
      fontSize: '16px',
      marginLeft: '2px',
      fontFamily: 'Source Sans Pro, sans-serif'
    }
  };

  return (
    <FormControl>
      <RadioGroup
        ref={innerRef}
        aria-labelledby={groupLabel?.toLowerCase().split(' ').join('-')}
        name="radio-buttons-group"
        onChange={(e) => onInternalChange(e.target.value)}
        sx={{ padding: '8px 0px 10px 12px', flexDirection: directionRow ? 'row' : 'column' }}
        value={value}
      >
        {options.map((option, index) => (
          <React.Fragment key={`${option.label}-${String(option.value)}`}>
            <FormControlLabel
              control={<Radio sx={radioStyle} />}
              disabled={!!draggable}
              id={option.label.toLowerCase().split(' ').join('-')}
              label={option.label}
              sx={labelStyle}
              value={draggable ? 'disabled' : option.value}
            />
            {options.findIndex((choice) => Number(value) === choice.value) === index &&
              index !== 0 && (
                <MultilineFormTextField
                  placeholder="Please type your selected response into this box and insert names and details where required"
                  required={question.required}
                  rows={4}
                  styles={{ marginLeft: '30px', width: 'calc(100% - 50px)' }}
                  value={inputText}
                  onChange={(e) => {
                    if (setApprovalFormQuestions) {
                      setApprovalFormQuestions((prev) => ({
                        ...prev,
                        [question.id]: {
                          ...prev[question.id],
                          inputText: e.target.value
                        }
                      }));
                    }
                  }}
                />
              )}
          </React.Fragment>
        ))}
        {error ? <Box sx={sharedClasses.errorBoxCheckbox}>{error}</Box> : null}
      </RadioGroup>
    </FormControl>
  );
};

export default FormRadioButtonWithTextInputGroup;
